import React, { memo, useMemo } from 'react'
import PropTypes from 'prop-types'
import {
  useFilters,
  useGlobalFilter,
  useGroupBy,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table'
import { FormGroup, GlobalFilter, NoticeMessage } from '../../../../../../../components'

import { RenderIf } from '../../../../../../../utils/fns'
import { useLazyEffect } from '../../../../../../../utils/hooks'

import '../../../../../../../globals/scss/components/tableview-virtualized.scss'

const ResultTable = (props) => {
  const {
    columns,
    data,
    getHeaderProps,
    getColumnProps,
    getRowProps,
    getCellProps,
    sortBy,
    searchKeyword,
    setSearchKeyword,
    optimized,
    setOptimized,
    getRowId,
    selectedRowIds,
    getTableData,
    setSelectedRow,
  } = props
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setSortBy, state } = useTable(
    {
      columns,
      data,
      getRowId,
      initialState: {
        sortBy,
        globalFilter: searchKeyword,
        selectedRowIds,
        hiddenColumns: [
          'tfNailQty',
          'tfNailType',
          'hNailQty',
          'hNailType',
          'jNailType',
          'jNailQty',
          'updatedSpec',
          'updatedWebStiffeners',
          'iciLabel'
        ],
      },
      useControlledState: (state) =>
        useMemo(
          () => ({ ...state, globalFilter: searchKeyword, selectedRowIds }),
          [state, searchKeyword, selectedRowIds],
        ),
    },
    useFilters,
    useGlobalFilter,
    useGroupBy,
    useSortBy,
    useRowSelect,
  )

  const renderedRows = useMemo(
    () => (optimized && rows.length > 10 ? rows.slice(0, 10) : rows),
    [rows, optimized],
  )

  useLazyEffect(() => {
    if (optimized) setSortBy(sortBy)
  }, [optimized])

  useLazyEffect(() => {
    getTableData(renderedRows.map((item) => ({
      id: item.id,
      isSelected: item.isSelected,
      seriesIllustration: item.original.seriesIllustration,
      spec: item.original.spec,
      ...item.values
    })) ?? [])
  }, [optimized, state])

  useLazyEffect(() => {
    if (
      Object.keys(selectedRowIds).length && !renderedRows.find(item => item.id === Object.keys(selectedRowIds)[0])
    ) {
      setSelectedRow()
    }
  }, [renderedRows])

  return (
    <div>
      <div className="tableInfoBar">
        <GlobalFilter setGlobalFilter={setSearchKeyword} globalFilter={state.globalFilter} />

        <div className="tableResults">
          <FormGroup className="tableToggleSwitch">
            <span>Show Optimized Models</span>
            <input
              type="checkbox"
              className="toggleSwitchInput"
              name="optimized"
              checked={optimized}
              onChange={setOptimized}
            />
          </FormGroup>
        </div>

        <div className="tableResults">
          {`Showing ${renderedRows.length} entr${renderedRows.length > 1 ? 'ies' : 'y'}`}
        </div>
      </div>
      <div className="tableInfoBar printView">
        <span>{searchKeyword && `Search: ${searchKeyword}`}</span>
        <span>Show Optimized Models: {optimized ? 'Yes' : 'No'}</span>
      </div>
      <div className="nonScrollable hs__nonScrollable">
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => {
              const { key, ...restHeaderProps } = headerGroup.getHeaderGroupProps()

              return (
                <tr key={key} {...restHeaderProps}>
                  {headerGroup.headers.map((column) => {
                    const { key, ...restColumnProps } = column.getHeaderProps([
                      {
                        className: column.className,
                        style: column.style,
                      },
                      getColumnProps(column),
                      getHeaderProps(column),
                      column.getSortByToggleProps(),
                    ])
                    return (
                      <th
                        key={key}
                        {...restColumnProps}
                        className={`th ${column.isSorted
                          ? column.isSortedDesc
                            ? '-sortDescending'
                            : '-sortAccending'
                          : ''
                          }`}
                      >
                        {column.render('Header')}
                      </th>
                    )
                  })}
                </tr>
              )
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {renderedRows.map((row, i) => {
              prepareRow(row)
              return (
                <tr key={i} {...row.getRowProps(getRowProps(row))}>
                  {row.cells.map((cell) => {
                    const { key, ...restCellProps } = cell.getCellProps([
                      {
                        className: cell.column.className,
                        style: cell.column.style,
                      },
                      getColumnProps(cell.column),
                      getCellProps(cell),
                    ])
                    return (
                      <td key={key} {...restCellProps}>
                        {cell.render('Cell')}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>

        {RenderIf(
          !renderedRows.length,
          <NoticeMessage
            level="warning"
            title="No Matching Records Found"
            description="Try editing or deleting your search criteria."
          />,
        )}
      </div>
    </div>
  )
}

ResultTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  data: PropTypes.arrayOf(PropTypes.any).isRequired,
  optimized: PropTypes.bool.isRequired,
  setOptimized: PropTypes.func.isRequired,
  searchKeyword: PropTypes.string.isRequired,
  setSearchKeyword: PropTypes.func.isRequired,
  sortBy: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  getRowId: PropTypes.func.isRequired,
  selectedRowIds: PropTypes.objectOf(PropTypes.any),
  getHeaderProps: PropTypes.func,
  getColumnProps: PropTypes.func,
  getRowProps: PropTypes.func,
  getCellProps: PropTypes.func,
  getTableData: PropTypes.func,
  setSelectedRow: PropTypes.func,
}

ResultTable.defaultProps = {
  loading: false,
  sortBy: [],
  getHeaderProps: () => ({}),
  getColumnProps: () => ({}),
  getRowProps: () => ({}),
  getCellProps: () => ({}),
}

export default memo(ResultTable)
